import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { iconStopwatch } from '@/components/icons';
import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { baseStyles, skewedLabelStyles } from '@/styles';
import { LayoutType } from '@/types';

@customElement('fg-hsp-label')
@localized()
export class FgHspLabel extends LitElement {
  @property({ type: Number })
  hspMaximumQuantity?: number | string | null = 0;

  @property({ type: String })
  layout: LayoutType = 'retail';

  @property({ type: Boolean })
  onlySkewedLabel = false;

  @property({ type: Boolean })
  hasLargeOnlySkewedLabel = false;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  renderSkewedLabel() {
    if (this.layout === 'retail') {
      return html`
        <span
          class="hsp-label ${classMap({
            collapsible: Boolean(this.configuratorContext?.quantityCollapsible),
          })}"
        >
          ${iconStopwatch}
          ${t(
            this.hasLargeOnlySkewedLabel ? 'labels.quickLarge' : 'labels.quick',
          )}
        </span>
      `;
    }

    if (this.hasLargeOnlySkewedLabel) {
      return html`
        <span class="skewed-label">
          <span class="content">${iconStopwatch} ${t('labels.24h')}</span>
        </span>
      `;
    }

    return html`
      <span class="skewed-label">
        <span class="content">${iconStopwatch} ${t('labels.quick')}</span>
      </span>
    `;
  }

  render() {
    if (this.onlySkewedLabel) {
      return this.hasLargeOnlySkewedLabel
        ? this.renderSkewedLabel()
        : html`
            <div class="small">${this.renderSkewedLabel()}</div>
          `;
    }

    if (!this.hspMaximumQuantity) {
      return nothing;
    }

    return html`
      <span class="skewed-label-wrapper">
        ${this.renderSkewedLabel()}
        <span class="max-quick-delivery">
          ${typeof this.hspMaximumQuantity === 'number'
            ? t('labels.maxQuickDelivery', {
                count: this.hspMaximumQuantity,
              })
            : this.hspMaximumQuantity}
        </span>
      </span>
    `;
  }

  static styles = [
    baseStyles,
    skewedLabelStyles,
    css`
      .hsp-label {
        align-items: center;
        display: flex;
        background-color: var(
          --fg-hsp-background,
          var(--fg-fallback-hsp-primary)
        );
        color: var(--fg-hsp-color, var(--fg-fallback-hsp-color));
        padding: 0.375rem 0.5rem;
        border-radius: 1.5rem;
        gap: 0.25rem;
        svg {
          fill: var(--fg-hsp-color, var(--fg-fallback-hsp-color));
        }
        width: fit-content;
      }

      .hsp-label.collapsible {
        display: block;
        font-size: var(--fg-font-size--xs, var(--fg-fallback-font-size--xs));
        font-weight: normal;
        height: 20px;
        line-height: 20px;
        padding: 0 0.375rem;
        svg {
          height: 12px;
          position: relative;
          top: 1px;
          width: 12px;
        }
        white-space: nowrap;
      }

      .skewed-label-wrapper {
        align-items: center;
        display: flex;
        gap: 0.5rem;

        .skewed-label {
          margin-right: 0.25rem;
        }

        .max-quick-delivery::first-letter {
          text-transform: uppercase;
        }
      }
    `,
  ];
}
